import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import HomeIcon from '@/shared/assets/home-icon.svg?react';
import ExchangeIcon from '@/shared/assets/exchange-rounded-icon.svg?react';
import MarketsIcon from '@/shared/assets/markets-icon.svg?react';
import ClockIcon from '@/shared/assets/clock-icon.svg?react';
import OpenNewTabRoundedIcon from '@/shared/assets/open-new-tab-rounded-icon.svg?react';
import CandlesIcon from '@/shared/assets/candles-icon.svg?react';
import OtcExchangeIcon from '@/shared/assets/otc-exchange-icon.svg?react';
import type { NavItem } from './types';
import { useLocation } from 'react-router-dom';
import { useUserScopeConfiguration } from '@/entities/user';
import { useAuthorizedTradingUrl } from '@/shared/api/mutations/useAuthorizedTradingUrl';
import { useSessionStore } from '@/entities/session';
import { useVerificationInfoModal } from '@/entities/verification';

const useNavigationItems = () => {
  const { t } = useTranslation();
  const { session } = useSessionStore();
  const activeNavItem = useLocation().pathname;
  const { userScopeConfiguration } = useUserScopeConfiguration();
  const { openVerificationInfoModal } = useVerificationInfoModal();
  const { getUrl } = useAuthorizedTradingUrl();

  const isVerified = session?.additional?.isVerified || false;

  const parseRoute = useCallback(
    (href: string, onClick?: () => void) => {
      if (!isVerified) {
        return {
          href: '',
          onClick: openVerificationInfoModal,
        };
      }

      return {
        href,
        onClick,
      };
    },
    [isVerified, openVerificationInfoModal]
  );

  const navItems = useMemo(() => {
    const items: NavItem[] = [
      {
        icon: HomeIcon,
        label: t('personalAccount.sidebarNavigation.home'),
        href: '/',
      },
      {
        icon: ExchangeIcon,
        ...parseRoute('/exchange'),
        label: t('personalAccount.sidebarNavigation.exchange'),
      },
      {
        icon: OtcExchangeIcon,
        label: t('personalAccount.sidebarNavigation.otcExchange'),
        ...parseRoute('/otc'),
        display: Boolean(userScopeConfiguration?.exchange.otc),
      },
      {
        icon: CandlesIcon,
        label: t('personalAccount.sidebarNavigation.trading'),
        ...parseRoute('', getUrl),
        external: true,
        rightIcon: OpenNewTabRoundedIcon,
      },

      {
        icon: MarketsIcon,
        label: t('personalAccount.sidebarNavigation.markets'),
        href: '/markets',
        //not in mvp
        display: false,
      },
      {
        icon: ClockIcon,
        label: t('personalAccount.sidebarNavigation.history'),
        href: '/history',
        activePatterns: [
          '/history',
          '/history/statement',
          '/ru/history/statement',
          '/en/history/statement',
        ],
      },
    ];
    return items.filter(item => item.display === undefined || item.display);
  }, [t, userScopeConfiguration, isVerified]);

  return { navItems, activeNavItem };
};

export { useNavigationItems };
